// Portable WebCrypto helpers — identical behaviour in the browser, the
// Cloudflare Worker, and the Node CLI (all expose globalThis.crypto.subtle).
//
// Key model (envelope encryption):
//   master   = PBKDF2-SHA256(password, kdf_salt, iters)        // the one slow step
//   kek      = HKDF(master, "kek")    // unwraps the DEK
//   authKey  = HKDF(master, "auth")   // server stores SHA-256(authKey) only
//   DEK      = 32 random bytes        // encrypts every secret + attachment
//   wrapped  = AES-256-GCM(kek, DEK)  // stored server-side
//
// The server only ever sees: kdf_salt, iters, SHA-256(authKey), and ciphertext.
// It can never derive kek, the DEK, or read any secret.

const te = new TextEncoder();
const td = new TextDecoder();

export const PBKDF2_ITERS = 600_000;
const HASH = "SHA-256";
const KEK_INFO = "localpass:kek:v1";
const AUTH_INFO = "localpass:auth:v1";

export interface Sealed {
  iv: Uint8Array; // 12-byte AES-GCM nonce
  ct: Uint8Array; // ciphertext WITH the appended 16-byte GCM tag
}

export interface DerivedKeys {
  kek: CryptoKey; // AES-GCM key used to wrap/unwrap the DEK
  authKey: Uint8Array; // raw 32 bytes, sent to the server to prove knowledge
}

export function randomBytes(len: number): Uint8Array {
  const b = new Uint8Array(len);
  crypto.getRandomValues(b);
  return b;
}

async function deriveMaster(
  password: string,
  salt: Uint8Array,
  iters: number,
): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey(
    "raw",
    te.encode(password.normalize("NFKC")),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations: iters, hash: HASH },
    base,
    256,
  );
  // Re-import the 256-bit master as an HKDF key for cheap domain separation.
  return crypto.subtle.importKey("raw", bits, "HKDF", false, ["deriveBits"]);
}

async function hkdf(
  master: CryptoKey,
  info: string,
  bytes = 32,
): Promise<Uint8Array> {
  const out = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: HASH, salt: new Uint8Array(0), info: te.encode(info) },
    master,
    bytes * 8,
  );
  return new Uint8Array(out);
}

/** Derive the KEK and auth key from a master password. The expensive part. */
export async function deriveKeys(
  password: string,
  salt: Uint8Array,
  iters: number = PBKDF2_ITERS,
): Promise<DerivedKeys> {
  const master = await deriveMaster(password, salt, iters);
  const [kekRaw, authKey] = await Promise.all([
    hkdf(master, KEK_INFO, 32),
    hkdf(master, AUTH_INFO, 32),
  ]);
  const kek = await crypto.subtle.importKey("raw", kekRaw, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
  return { kek, authKey };
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", data));
}

export async function aesEncrypt(
  key: CryptoKey,
  plaintext: Uint8Array,
): Promise<Sealed> {
  const iv = randomBytes(12);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext),
  );
  return { iv, ct };
}

export async function aesDecrypt(
  key: CryptoKey,
  sealed: Sealed,
): Promise<Uint8Array> {
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: sealed.iv },
    key,
    sealed.ct,
  );
  return new Uint8Array(pt);
}

// ---- DEK helpers -----------------------------------------------------------

export function generateDek(): Uint8Array {
  return randomBytes(32);
}

export async function importDek(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

export function wrapDek(kek: CryptoKey, dekRaw: Uint8Array): Promise<Sealed> {
  return aesEncrypt(kek, dekRaw);
}

export async function unwrapDek(
  kek: CryptoKey,
  sealed: Sealed,
): Promise<CryptoKey> {
  const raw = await aesDecrypt(kek, sealed); // throws if wrong password (GCM auth fails)
  return importDek(raw);
}

// ---- encoding helpers ------------------------------------------------------

export function toHex(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += x.toString(16).padStart(2, "0");
  return s;
}

export function fromHex(s: string): Uint8Array {
  const a = new Uint8Array(s.length / 2);
  for (let i = 0; i < a.length; i++) a[i] = parseInt(s.substr(i * 2, 2), 16);
  return a;
}

export function toB64(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s);
}

export function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a;
}

export function encodeJson(obj: unknown): Uint8Array {
  return te.encode(JSON.stringify(obj));
}

export function decodeJson<T>(b: Uint8Array): T {
  return JSON.parse(td.decode(b)) as T;
}
